import React, { useRef, useEffect, useState } from 'react';
import styles from './TokenomicsSection.module.scss';
import { motion } from 'framer-motion';

const TokenomicsSection = () => {
  const ref = useRef();
  const chartRef = useRef();
  const [visible, setVisible] = useState(false);
  const [activeSegment, setActiveSegment] = useState(null);

  // Tokenomics allocation data
  const tokenomicsData = [
    { id: 'team', label: 'Team & Advisors', percentage: 20, color: '#6ED0C0' },
    { id: 'investors', label: 'Investors', percentage: 30, color: '#00A6BB' },
    { id: 'community', label: 'Community Rewards', percentage: 15, color: '#16526B' },
    { id: 'ecosystem', label: 'Ecosystem Growth', percentage: 25, color: '#80C9BA' },
    { id: 'liquidity', label: 'Liquidity Pool', percentage: 10, color: '#2B7C88' },
  ];

  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      if (rect.top < window.innerHeight - 100) setVisible(true);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (visible && chartRef.current) {
      renderDonutChart();
    }
  }, [visible]);

  const renderDonutChart = () => {
    if (!chartRef.current) return;
    
    // Clear previous chart if any
    while (chartRef.current.firstChild) {
      chartRef.current.removeChild(chartRef.current.firstChild);
    }
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('width', '100%');
    svg.setAttribute('height', '100%');
    
    const radius = 35;
    const cx = 50;
    const cy = 50;
    
    let currentAngle = 0;
    
    tokenomicsData.forEach((segment, index) => {
      const angleSize = (segment.percentage / 100) * 360;
      const endAngle = currentAngle + angleSize;
      
      // Convert angles to radians for calculations
      const startAngleRad = (currentAngle - 90) * (Math.PI / 180);
      const endAngleRad = (endAngle - 90) * (Math.PI / 180);
      
      const startX = cx + radius * Math.cos(startAngleRad);
      const startY = cy + radius * Math.sin(startAngleRad);
      const endX = cx + radius * Math.cos(endAngleRad);
      const endY = cy + radius * Math.sin(endAngleRad);
      
      // Create arc path
      const largeArcFlag = angleSize > 180 ? 1 : 0;
      const pathData = [
        `M ${cx} ${cy}`,
        `L ${startX} ${startY}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        'Z'
      ].join(' ');
      
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', pathData);
      path.setAttribute('fill', segment.color);
      path.setAttribute('stroke', '#14151A');
      path.setAttribute('stroke-width', '1');
      path.setAttribute('class', styles.chartSegment);
      path.setAttribute('data-segment-id', segment.id);
      
      path.addEventListener('mouseenter', () => setActiveSegment(segment.id));
      path.addEventListener('mouseleave', () => setActiveSegment(null));
      
      svg.appendChild(path);
      currentAngle = endAngle;
    });
    
    // Center circle
    const centerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    centerCircle.setAttribute('cx', cx);
    centerCircle.setAttribute('cy', cy);
    centerCircle.setAttribute('r', radius * 0.6);
    centerCircle.setAttribute('fill', '#14151A');
    centerCircle.setAttribute('stroke', 'rgba(255, 255, 255, 0.1)');
    centerCircle.setAttribute('stroke-width', '1');
    
    svg.appendChild(centerCircle);
    chartRef.current.appendChild(svg);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1,
      transition: { 
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };
  
  const chartVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.8, delay: 0.3 }
    }
  };

  return (
    <section ref={ref} id="tokenomics" className={styles.section}>
      <motion.h2 
        className={styles.title}
        initial={{ opacity: 0, y: -15 }}
        animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: -15 }}
        transition={{ duration: 0.8 }}
      >
        Tokenomics at a Glance
      </motion.h2>
      
      <div className={styles.container}>
        <motion.div 
          className={styles.chartContainer}
          initial="hidden"
          animate={visible ? "visible" : "hidden"}
          variants={chartVariants}
        >
          <div ref={chartRef} className={styles.chart}></div>
          <div className={styles.chartInfo}>
            {activeSegment ? (
              <div className={styles.segmentInfo}>
                <span className={styles.segmentLabel} style={{ 
                  color: tokenomicsData.find(item => item.id === activeSegment)?.color 
                }}>
                  {tokenomicsData.find(item => item.id === activeSegment)?.label}
                </span>
                <span className={styles.segmentPercentage}>
                  {tokenomicsData.find(item => item.id === activeSegment)?.percentage}%
                </span>
              </div>
            ) : (
              <div className={styles.totalInfo}>
                <span className={styles.totalLabel}>Total Supply</span>
                <span className={styles.totalValue}>100,000,000 PRPT</span>
              </div>
            )}
          </div>
          
          <div className={styles.legend}>
            {tokenomicsData.map((item) => (
              <div 
                key={item.id}
                className={styles.legendItem}
                onMouseEnter={() => setActiveSegment(item.id)}
                onMouseLeave={() => setActiveSegment(null)}
              >
                <span 
                  className={styles.legendColor} 
                  style={{ backgroundColor: item.color }}
                ></span>
                <span className={styles.legendLabel}>{item.label}</span>
                <span className={styles.legendPercentage}>{item.percentage}%</span>
              </div>
            ))}
          </div>
        </motion.div>
        
        <motion.div 
          className={styles.grid}
          initial="hidden"
          animate={visible ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <motion.div className={`${styles.card} ${visible ? styles.visible : ''}`} variants={cardVariants}>
            <motion.div className={styles.iconContainer} variants={iconVariants}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM9.97 9H8V15H9.97V9ZM11.97 9H14.03C14.23 9 14.39 9.11 14.45 9.29L15.45 12.68C15.487 12.792 15.487 12.918 15.45 13.03L14.45 16.43C14.393 16.622 14.215 16.76 14.01 16.76H11.99C11.785 16.76 11.607 16.622 11.55 16.43L10.55 13.03C10.518 12.917 10.518 12.793 10.55 12.68L11.55 9.29C11.61 9.11 11.77 9 11.97 9ZM16 9H18V15H16V9Z" fill="currentColor" />
              </svg>
            </motion.div>
            <motion.h3 
              className={styles.cardTitle}
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              $5M Raise
            </motion.h3>
            <motion.p 
              className={styles.cardDescription}
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              Total raise of $6M ($1M Seed)
            </motion.p>
          </motion.div>
          
          <motion.div className={`${styles.card} ${visible ? styles.visible : ''}`} variants={cardVariants}>
            <motion.div className={styles.iconContainer} variants={iconVariants}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.8 10.9C9.53 10.31 8.8 9.7 8.8 8.75C8.8 7.66 9.81 6.9 11.5 6.9C13.28 6.9 13.94 7.75 14 9H16.21C16.14 7.28 15.09 5.7 13 5.19V3H10V5.16C8.06 5.58 6.5 6.84 6.5 8.77C6.5 11.08 8.41 12.23 11.2 12.9C13.7 13.5 14.2 14.38 14.2 15.31C14.2 16 13.71 17.1 11.5 17.1C9.44 17.1 8.63 16.18 8.5 15H6.32C6.44 17.19 8.08 18.42 10 18.83V21H13V18.85C14.95 18.48 16.5 17.35 16.5 15.3C16.5 12.46 14.07 11.49 11.8 10.9Z" fill="currentColor" />
              </svg>
            </motion.div>
            <motion.h3 
              className={styles.cardTitle}
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              $0.10 & $0.15
            </motion.h3>
            <motion.p 
              className={styles.cardDescription}
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              $0.10 or $0.15 per coin depending on vesting schedule
            </motion.p>
          </motion.div>
          
          <motion.div className={`${styles.card} ${visible ? styles.visible : ''}`} variants={cardVariants}>
            <motion.div className={styles.iconContainer} variants={iconVariants}>
              <svg className={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.5 18.49L9.5 12.48L13.5 16.48L22 6.92L20.59 5.51L13.5 13.48L9.5 9.48L2 16.99L3.5 18.49Z" fill="currentColor" />
              </svg>
            </motion.div>
            <motion.h3 
              className={styles.cardTitle}
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              Huge potential
            </motion.h3>
            <motion.p 
              className={styles.cardDescription}
              initial={{ opacity: 0 }}
              animate={visible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              Early investment positions you in a disruptive market shift in property
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
      
      <motion.div 
        className={styles.vestingDetails}
        initial={{ opacity: 0, y: 20 }}
        animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        <h3 className={styles.vestingTitle}>Strategic Vesting Schedule</h3>
        <div className={styles.vestingTable}>
          <div className={styles.vestingTableHeader}>
            <span>Allocation</span>
            <span>Tokens</span>
            <span>Vesting Period</span>
            <span>Cliff</span>
          </div>
          <div className={styles.vestingTableRow}>
            <span>Team & Advisors</span>
            <span>20,000,000</span>
            <span>36 months</span>
            <span>12 months</span>
          </div>
          <div className={styles.vestingTableRow}>
            <span>Investors</span>
            <span>30,000,000</span>
            <span>24 months</span>
            <span>6 months</span>
          </div>
          <div className={styles.vestingTableRow}>
            <span>Community</span>
            <span>15,000,000</span>
            <span>48 months</span>
            <span>None</span>
          </div>
          <div className={styles.vestingTableRow}>
            <span>Ecosystem</span>
            <span>25,000,000</span>
            <span>36 months</span>
            <span>3 months</span>
          </div>
          <div className={styles.vestingTableRow}>
            <span>Liquidity</span>
            <span>10,000,000</span>
            <span>12 months</span>
            <span>None</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default TokenomicsSection; 